import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import Sidenav from '@brands/Dashboard/Sidenav/Sidenav';
import { useWindowSize } from '@brands/hooks';
import { sendResetEmail } from '@brands/services/auth/sendResetEmail';
import { yupResolver } from '@hookform/resolvers/yup';
import TextButton from '@mui/material/Button';
import React, { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import Button from '../../Components/Button/Button';
import { customStylesForgotPassword } from '../../Utils/customStyles';
import { displayErrorDetails } from '../../Utils/displayError';
import styles from './styles.module.scss';
import leftArrow from './utils/icons/leftArrow.svg';

type ForgotPasswordCredentials = {
  username?: string;
  code: string;
  new_password: string;
  reset_password_hash?: string;
};

interface ForgotPasswordModalStepTwoProps {
  forgotPasswordStep: number;
  setForgotPasswordStep: (num: number) => void;
  credentials: ForgotPasswordCredentials;
  setCredentials: (credentials: ForgotPasswordCredentials) => void;
  resetFlag: boolean;
}

type ForgotPasswordModalStepTwoSubmitForm = {
  code?: string;
};

const validationSchema = Yup.object().shape({
  code: Yup.string()
    .required('Code is required')
    .test('test-name', 'Enter a valid 6-digit code', (value: string | undefined) => {
      const codeRegex = /^[0-9]{6}$/;
      return codeRegex.test(value as string);
    }),
});

const ForgotPasswordModalStepTwo: FC<ForgotPasswordModalStepTwoProps> = ({
  forgotPasswordStep,
  setForgotPasswordStep,
  credentials,
  setCredentials,
  resetFlag,
}) => {
  const navigate = useNavigate();
  const isMobile = useWindowSize().width < 992;
  const [loading, setLoading] = useState(false);

  const closeModal = (): void => {
    setForgotPasswordStep(0);
    setCredentials({
      username: '',
      code: '',
      new_password: '',
    });
  };

  useEffect(() => {
    if (forgotPasswordStep === 2) {
      document.body.classList.add('modal-open');
    } else if (document.body.classList.contains('modal-open') && forgotPasswordStep === 0) {
      document.body.classList.remove('modal-open');
    }
  }, [forgotPasswordStep]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ForgotPasswordModalStepTwoSubmitForm>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = (data: ForgotPasswordModalStepTwoSubmitForm): void => {
    setCredentials({ ...credentials, code: data.code as string });
    setForgotPasswordStep(3);
  };

  const resendCode = async (): Promise<void> => {
    try {
      setLoading(true);
      await sendResetEmail({ email: credentials.username as string });
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <Sidenav isVisibleOnMobile hideDefaultOptions authLoog hideModals={() => setForgotPasswordStep(0)} />
      <Modal
        isOpen={forgotPasswordStep === 2}
        onRequestClose={closeModal}
        style={customStylesForgotPassword(isMobile, '40%')}
        contentLabel="Modal"
        ariaHideApp={false}
        className={`${styles.wrapper} ${styles.forgotPasswordModal}`}
        shouldCloseOnOverlayClick={false}
      >
        <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
          <span className={`${styles.formTitle} p-b-26`}>
            <span className={`${styles.formTitleForgot} fs-unmask`}>
              {resetFlag ? 'Reset your password' : 'Forgot Password'}
            </span>
            <div className={`text-center ${styles.formTitleLink} p-t-115 fs-unmask`}>
              <span className={`${styles.txt1} txt1`}>Please enter the 6-digit code that we sent to your email.</span>
            </div>
          </span>
          <div className={styles.inputWrapper}>
            <TextField
              {...register('code')}
              label="Verification Code"
              errors={errors.code ? errors : {}}
              name="code"
              onChange={(e) => {
                setValue('code', e.target.value, { shouldValidate: true });
              }}
              inputClass={`${styles.profileInputClass} fs-exclude`}
              wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
            />
          </div>
          <div className={styles.buttonContainer}>
            <div className={styles.buttonWrapper}>
              <Button type="submit" className={styles.formBtn}>
                Submit
              </Button>
            </div>
          </div>
          <div className={styles.accessToEmail}>
            <span>Didn&apos;t get the code?&nbsp;</span>
            <button
              type="button"
              onClick={resendCode}
              disabled={loading}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: 'inherit',
                color: 'inherit',
              }}
            >
              Resend
            </button>
          </div>
          <div className={`${styles.backArrow}`} style={{ marginTop: '0px' }}>
            <TextButton
              variant="text"
              onClick={() => {
                closeModal();
                navigate('/');
              }}
            >
              <img src={leftArrow} alt="left arrow" />
              <span>back to sign in</span>
            </TextButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

Modal.setAppElement('#root');

export default ForgotPasswordModalStepTwo;
